import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import Typography from 'kingpin/atoms/Typography';
import Tabs from 'components/Tabs';

import AddTabButton from './AddTabButton';
import ManageTabsButton from './ManageTabsButton';
import { TopBarDiv } from '../../TargetsAppShow/TargetsAppConfigured/TopBar';
import EntityDetailsContext from '../EntityDetailsContext';
import Inputs from 'components/Inputs';
import CommentsButton from './CommentsButton';
import PermissionGates from 'components/PermissionGates';
import PERMISSIONS from '../../../permissionDefinitions';
import Row from '../../../components/Common/Row';

import { BonusAppContext } from '../../../contextProviders/BonusAppProvider';

import { MappedUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/MappedUserContentSettingsProvider';
import { CurrentUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/CurrentUserContentSettingsProvider';
import CurrentUserContext from '../../../contexts/CurrentUserContext';
import ImpersonatorContext from '../../../contexts/ImpersonatorContext';
import Button from '../../../kingpin/atoms/Button';
import { UIVersionContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import PopupEntityAppContext from '../../../contextProviders/SplashScreenProviders/MiscProviders/PopupEntityAppProvider/PopupEntityAppContext';
import { useLocation } from 'react-router-dom';

const Control = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 8px;

  &:last-child {
    margin-right: 0px;
  }
`;

export const EntityOptionsDropdown = () => {
  const entityDetails = useContext(EntityDetailsContext);
  const bonusAppContext = useContext(BonusAppContext);
  if (!entityDetails) {
    return null;
  }

  if (bonusAppContext) {
    return null;
  }

  return (
    <Inputs.Dropdown
      options={entityDetails.entityOptions}
      controlledSearchText={entityDetails.entitySearchText}
      controlledOnSearchTextChanged={entityDetails.onEntitySearchTextChanged}
      isSearchEnabled
      isLoading={entityDetails.isLoadingEntityOptions}
      testId={`select-entity`}
    />
  );
};

const RenderSelectedViewLabel = ({
  selectedOption,
}: {
  selectedOption?: DropdownOption;
}) => {
  if (!selectedOption) {
    return null;
  }

  return (
    <Row centerAlign style={{ gap: 4, overflow: 'hidden' }}>
      <Typography.Body type={'Label'} color={'var(--color-grey-5)'}>
        Viewing as:
      </Typography.Body>
      <Typography.Body type={'Label'} isEllipsis>
        {selectedOption.label}
      </Typography.Body>
    </Row>
  );
};

/**
 * This drives {@link useSelectedEntityFilter}
 */
export const MappedUsersDropdown = () => {
  const { pathname } = useLocation();
  const { id: baseCurrentUserId } = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const currentUserId = impersonatorId ? impersonatorId : baseCurrentUserId;
  const { isV3 } = useContext(UIVersionContext);

  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const {
    selectedMappedUser,
    onMappedUserSelected,
    mappedUsersForCurrentPortal,
  } = useContext(MappedUserContentSettingsContext);

  const options = useMemo((): DropdownOption[] => {
    if (
      contentSettings.mode === 'engagement' &&
      (!isV3 || pathname.includes('view'))
    ) {
      const newOptions: DropdownOption[] = [];
      const currentUserMapping = mappedUsersForCurrentPortal.find(
        (u) => u.user.id === currentUserId,
      );
      if (currentUserMapping) {
        newOptions.push({
          label: 'Me',
          isSelected:
            selectedMappedUser &&
            currentUserMapping.user.id === selectedMappedUser.user.id,
          onSelected: () => {
            onMappedUserSelected(currentUserMapping);
          },
        });
      }

      mappedUsersForCurrentPortal
        // Remove current user as that is captured by 'Me' above
        .filter((mappedUser) => mappedUser.user.id !== currentUserId)
        .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName))
        .forEach((mappedUser) => {
          newOptions.push({
            label: mappedUser.user.displayName,
            isSelected:
              selectedMappedUser &&
              selectedMappedUser.user.id === mappedUser.user.id,
            onSelected: () => {
              onMappedUserSelected(mappedUser);
            },
          });
        });
      return newOptions;
    }

    return [
      {
        label: 'None',
        isSelected: !selectedMappedUser,
        onSelected: () => {
          onMappedUserSelected(undefined);
        },
      },
      ...[...mappedUsersForCurrentPortal]
        .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName))
        .map((user) => ({
          label: user.user.displayName,
          isSelected:
            selectedMappedUser && selectedMappedUser.user.id === user.user.id,
          onSelected: () => {
            onMappedUserSelected(user);
          },
        })),
    ];
  }, [
    contentSettings.mode,
    isV3,
    pathname,
    selectedMappedUser,
    mappedUsersForCurrentPortal,
    currentUserId,
    onMappedUserSelected,
  ]);

  const selectedOption = useMemo(() => {
    return options.find((o) => o.isSelected);
  }, [options]);

  if (isV3) {
    if (options.length === 1 || !selectedOption) {
      return null;
    }

    return (
      <div style={{ marginBottom: 2 }}>
        <Inputs.Dropdown
          options={options}
          placeholder="Select a User"
          isSearchEnabled
          renderSelectedLabel={() => (
            <RenderSelectedViewLabel selectedOption={selectedOption} />
          )}
        />
      </div>
    );
  }

  return (
    <Inputs.Dropdown
      options={options}
      placeholder="Select a User"
      isSearchEnabled
    />
  );
};

/**
 * We use this space in a couple different ways, depending on the context.
 *
 * For a "simplified user", which is represented by a user
 * with a {@link UserManagement.EngagementContentSettings} against their account,
 * we want to show a picker which lets them switch between views of other
 * users they have been granted access to
 *
 * For an administrative user, such as a FleetManager looking at an
 * @{@link EngagementPortal}, we want to show them a dropdown to switch between all
 * users mapped to the portal.
 *
 * In an entity resource navigator app, which is accessible through a new tab
 * for all user types, including those above, we want to show a dropdown to go between
 * all possible entity values. eg: truck / driver.
 */
const EntityDropdown = () => {
  const { isMappedUserForCurrentEntity } = useContext(
    MappedUserContentSettingsContext,
  );

  if (isMappedUserForCurrentEntity) {
    return <MappedUsersDropdown />;
  }

  return <EntityOptionsDropdown />;
};

const TopBar = () => {
  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const entityDetails = useContext(EntityDetailsContext);
  const popupEntityAppContext = useContext(PopupEntityAppContext);
  const { mappedUsersForCurrentPortal } = useContext(
    MappedUserContentSettingsContext,
  );
  if (!entityDetails) {
    return null;
  }

  const { app, selectedEntity, selectedEntityLabel, tabs } = entityDetails;

  if (contentSettings.mode === 'normal' || !!popupEntityAppContext) {
    return (
      <>
        <TopBarDiv>
          <Row centerAlign style={{ gap: 8 }}>
            {popupEntityAppContext && (
              <Button
                size={'Small'}
                type={'Secondary'}
                icon={'cross'}
                onClick={popupEntityAppContext.closeEntityPopup}
              />
            )}
            <Typography.Header type="H5">
              {`${app.title}: ${selectedEntityLabel}`}
            </Typography.Header>
          </Row>
          <Row>
            <Control>
              <EntityDropdown />
            </Control>
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.WORKSPACES.ADD_REMOVE_TABS}
            >
              <Control>
                <div style={{ marginRight: 8 }}>
                  <AddTabButton app={app} />
                </div>
                <ManageTabsButton tabs={tabs} app={app} />
              </Control>
            </PermissionGates.Has>
            {selectedEntity && (
              <Control>
                <CommentsButton selectedEntity={selectedEntity} app={app} />
              </Control>
            )}
          </Row>
        </TopBarDiv>
        <TopBarDiv>
          <Tabs.TabsRow tabs={tabs} />
        </TopBarDiv>
      </>
    );
  }

  return (
    <>
      <TopBarDiv>
        <Tabs.TabsRow tabs={tabs} />
        {mappedUsersForCurrentPortal.length > 1 && (
          <Control>
            <EntityDropdown />
          </Control>
        )}
      </TopBarDiv>
    </>
  );
};

export default TopBar;
